/**
 * LoadingScreen Component
 * 
 * A 16-bit style loading screen that appears when the page is first loaded.
 * Features a progressive loading bar and animated "Loading awesome stuff..." text.
 * Uses the Press Start 2P font to match the site's monogram styling.
 */

import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

// Animation for the dots cycling
const dotAnimation = keyframes`
  0% { content: '.'; }
  33% { content: '..'; }
  66% { content: '...'; }
  100% { content: ''; }
`;

// Animation for the blinking effect of pixels in the loading bar
const pixelBlinkAnimation = keyframes`
  0% { opacity: 0.8; }
  50% { opacity: 1; }
  100% { opacity: 0.8; }
`;

// Fade out animation for the entire loading screen
const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; visibility: hidden; }
`;

// The main container for the loading screen
const LoadingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #1d2021; /* Gruvbox background */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  animation: ${fadeOut} 0.5s ease-in-out forwards;
  animation-delay: 2.5s;
`;

// The loading text with the animated dots
const LoadingText = styled.div`
  font-family: 'Press Start 2P', cursive;
  color: #ebdbb2; /* Gruvbox foreground */
  font-size: 1.2rem;
  margin-bottom: 2rem;
  position: relative;
  text-align: center;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    animation: ${dotAnimation} 1s infinite;
  }
`;

// The outer container for the loading bar
const LoadingBarContainer = styled.div`
  width: 80%;
  max-width: 400px;
  height: 30px;
  background-color: #3c3836; /* Gruvbox dark */
  border: 4px solid #a89984; /* Gruvbox foreground */
  padding: 4px;
  position: relative;
  
  @media (max-width: 768px) {
    width: 90%;
    max-width: 300px;
    height: 24px;
    border-width: 3px;
    padding: 3px;
  }
`;

// The inner progress bar that fills up with pixelated 16-bit movement
const LoadingBarProgress = styled.div`
  height: 100%;
  background-color: #b8bb26; /* Gruvbox green - matches your primary color */
  width: ${props => (props.progress * 100 / 16)}%;
  image-rendering: pixelated;
  position: relative;
  transition: none; /* Remove smooth transitions for authentic 16-bit look */
  
  /* Create a pixel pattern for the 16-bit effect */
  background-image: linear-gradient(90deg, 
    rgba(255, 255, 255, 0.2) 25%, 
    transparent 25%, 
    transparent 50%, 
    rgba(255, 255, 255, 0.2) 50%, 
    rgba(255, 255, 255, 0.2) 75%, 
    transparent 75%);
  background-size: 16px 16px;
  animation: ${pixelBlinkAnimation} 0.5s infinite;
  
  @media (max-width: 768px) {
    background-size: 12px 12px;
  }
`;


// Pixel-style segments to create the 16-bit effect
const PixelSegment = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 6.25%; /* 16 segments (100/16) */
  border-right: 2px solid rgba(29, 32, 33, 0.5);
  pointer-events: none;
`;

/**
 * LoadingScreen component that displays a 16-bit style loading animation
 * when the site is first opened. It automatically disappears after the
 * loading animation completes.
 * 
 * @returns {JSX.Element} The loading screen component
 */
const LoadingScreen = () => {
  const [visible, setVisible] = useState(true);
  
  // Create the pixel segments for the 16-bit effect
  const renderPixelSegments = () => {
    const segments = [];
    for (let i = 1; i < 16; i++) {
      segments.push(
        <PixelSegment 
          key={i} 
          style={{ left: `${i * 6.25}%` }} 
        />
      );
    }
    return segments;
  };
  
  // Enhance the 16-bit loading experience with a chunky progress movement
  const [progress, setProgress] = useState(0);
  const [fontLoaded, setFontLoaded] = useState(false);
  
  // Simple font loading check using document.fonts if available (modern browsers)
  useEffect(() => {
    // Check if the browser supports the document.fonts API
    if (document.fonts && document.fonts.check) {
      // First attempt to check if font is already loaded
      if (document.fonts.check('1em "Press Start 2P"')) {
        setFontLoaded(true);
      } else {
        // If not loaded, set up a listener for when fonts are loaded
        document.fonts.ready.then(() => {
          setFontLoaded(true);
        }).catch(() => {
          // Fallback in case of error
          setTimeout(() => setFontLoaded(true), 1000);
        });
      }
    } else {
      // Fallback for browsers that don't support document.fonts
      // Just delay the loading screen appearance slightly
      setTimeout(() => setFontLoaded(true), 500);
    }
  }, []);
  
  // Progress bar animation with discrete steps to simulate 16-bit movement
  useEffect(() => {
    // 16 steps for the 16-bit feel
    const stepCount = 16;
    const stepTime = 2500 / stepCount; // 2.5 seconds total for the loading bar
    
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev < stepCount) {
          return prev + 1;
        }
        clearInterval(interval);
        return prev;
      });
    }, stepTime);
    
    // Hide the loading screen after the animation completes
    const timer = setTimeout(() => {
      setVisible(false);
    }, 3000); // 3 seconds total (2.5s for loading + 0.5s for fade out)
    
    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, []);
  
  if (!visible || !fontLoaded) return null;
  
  return (
    <LoadingContainer>
      <LoadingText>Loading awesome stuff</LoadingText>
      <LoadingBarContainer>
        <LoadingBarProgress progress={progress} />
        {renderPixelSegments()}
      </LoadingBarContainer>
    </LoadingContainer>
  );
};

export default LoadingScreen;
